/**
 * Compliance Settings Form Component
 *
 * Form for configuring compliance and security settings.
 * Includes data retention, privacy frameworks, and security policies.
 */
import { useState, useEffect } from 'react';
import {
  cn,
  Button,
  Input,
  Label,
  Switch,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@respectly/ui';
import { Shield, Loader2, AlertTriangle } from 'lucide-react';
import type {
  ComplianceSettings,
  UpdateComplianceSettingsDto,
} from '@respectly/shared-types';

// ============================================
// Component Props
// ============================================

export interface ComplianceSettingsFormProps {
  settings: ComplianceSettings | null;
  onSubmit: (data: UpdateComplianceSettingsDto) => Promise<boolean>;
  isLoading?: boolean;
  isSaving?: boolean;
  className?: string;
}

// ============================================
// Form State
// ============================================

interface FormState {
  // Data Retention
  dataRetentionDays: number;
  auditLogRetentionDays: number;
  // Compliance Frameworks
  gdprEnabled: boolean;
  hipaaEnabled: boolean;
  ccpaEnabled: boolean;
  // Privacy
  anonymizeDeletedUsers: boolean;
  requireConsentForMarketing: boolean;
  // Security
  enforcePasswordPolicy: boolean;
  passwordMinLength: number;
  passwordRequireUppercase: boolean;
  passwordRequireNumber: boolean;
  passwordRequireSpecial: boolean;
  sessionTimeoutMinutes: number;
  maxFailedLoginAttempts: number;
  lockoutDurationMinutes: number;
  // Audit
  logAllApiCalls: boolean;
  logSensitiveDataAccess: boolean;
}

const initialFormState: FormState = {
  dataRetentionDays: 365,
  auditLogRetentionDays: 730,
  gdprEnabled: false,
  hipaaEnabled: false,
  ccpaEnabled: false,
  anonymizeDeletedUsers: true,
  requireConsentForMarketing: true,
  enforcePasswordPolicy: true,
  passwordMinLength: 12,
  passwordRequireUppercase: true,
  passwordRequireNumber: true,
  passwordRequireSpecial: true,
  sessionTimeoutMinutes: 60,
  maxFailedLoginAttempts: 5,
  lockoutDurationMinutes: 15,
  logAllApiCalls: false,
  logSensitiveDataAccess: true,
};

// ============================================
// Component
// ============================================

export function ComplianceSettingsForm({
  settings,
  onSubmit,
  isLoading = false,
  isSaving = false,
  className,
}: ComplianceSettingsFormProps) {
  const [form, setForm] = useState<FormState>(initialFormState);
  const [errors, setErrors] = useState<Partial<Record<keyof FormState, string>>>({});

  // Load settings into form
  useEffect(() => {
    if (settings) {
      setForm({
        dataRetentionDays: settings.dataRetentionDays,
        auditLogRetentionDays: settings.auditLogRetentionDays,
        gdprEnabled: settings.gdprEnabled,
        hipaaEnabled: settings.hipaaEnabled,
        ccpaEnabled: settings.ccpaEnabled,
        anonymizeDeletedUsers: settings.anonymizeDeletedUsers,
        requireConsentForMarketing: settings.requireConsentForMarketing,
        enforcePasswordPolicy: settings.enforcePasswordPolicy,
        passwordMinLength: settings.passwordMinLength,
        passwordRequireUppercase: settings.passwordRequireUppercase,
        passwordRequireNumber: settings.passwordRequireNumber,
        passwordRequireSpecial: settings.passwordRequireSpecial,
        sessionTimeoutMinutes: settings.sessionTimeoutMinutes,
        maxFailedLoginAttempts: settings.maxFailedLoginAttempts,
        lockoutDurationMinutes: settings.lockoutDurationMinutes,
        logAllApiCalls: settings.logAllApiCalls,
        logSensitiveDataAccess: settings.logSensitiveDataAccess,
      });
    }
  }, [settings]);

  // Validate form
  const validate = (): boolean => {
    const newErrors: Partial<Record<keyof FormState, string>> = {};

    if (form.dataRetentionDays < 30 || form.dataRetentionDays > 3650) {
      newErrors.dataRetentionDays = 'Must be between 30 and 3650 days';
    }

    if (form.auditLogRetentionDays < 90 || form.auditLogRetentionDays > 3650) {
      newErrors.auditLogRetentionDays = 'Must be between 90 and 3650 days';
    }

    if (form.passwordMinLength < 8 || form.passwordMinLength > 32) {
      newErrors.passwordMinLength = 'Must be between 8 and 32 characters';
    }

    if (form.sessionTimeoutMinutes < 15 || form.sessionTimeoutMinutes > 1440) {
      newErrors.sessionTimeoutMinutes = 'Must be between 15 and 1440 minutes';
    }

    if (form.maxFailedLoginAttempts < 3 || form.maxFailedLoginAttempts > 10) {
      newErrors.maxFailedLoginAttempts = 'Must be between 3 and 10 attempts';
    }

    if (form.lockoutDurationMinutes < 5 || form.lockoutDurationMinutes > 60) {
      newErrors.lockoutDurationMinutes = 'Must be between 5 and 60 minutes';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) return;

    await onSubmit(form);
  };

  // Handle input change
  const handleChange = <K extends keyof FormState>(
    key: K,
    value: FormState[K]
  ) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    if (errors[key]) {
      setErrors((prev) => ({ ...prev, [key]: undefined }));
    }
  };

  // Handle number input change
  const handleNumberChange = (key: keyof FormState, value: string) => {
    const num = parseInt(value, 10);
    if (!isNaN(num)) {
      handleChange(key, num as FormState[typeof key]);
    }
  };

  if (isLoading) {
    return (
      <Card className={className}>
        <CardContent className="p-6">
          <div className="space-y-4 animate-pulse">
            <div className="h-10 bg-slate-200 dark:bg-slate-700 rounded" />
            <div className="h-10 bg-slate-200 dark:bg-slate-700 rounded" />
            <div className="h-10 bg-slate-200 dark:bg-slate-700 rounded" />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <form onSubmit={handleSubmit} className={className}>
      <div className="space-y-6">
        {/* Warning Banner */}
        <div className="flex items-start gap-3 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
          <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="font-medium text-amber-800 dark:text-amber-200">
              Compliance settings impact all tenants
            </h4>
            <p className="text-sm text-amber-700 dark:text-amber-300 mt-1">
              Changes to these settings will affect all accounts on the platform.
              Review carefully before saving.
            </p>
          </div>
        </div>

        {/* Data Retention */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Data Retention</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="dataRetentionDays">Data Retention (days)</Label>
                <Input
                  id="dataRetentionDays"
                  type="number"
                  min={30}
                  max={3650}
                  value={form.dataRetentionDays}
                  onChange={(e) => handleNumberChange('dataRetentionDays', e.target.value)}
                  className={cn(errors.dataRetentionDays && 'border-red-500')}
                />
                {errors.dataRetentionDays && (
                  <p className="text-xs text-red-500">{errors.dataRetentionDays}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="auditLogRetentionDays">Audit Log Retention (days)</Label>
                <Input
                  id="auditLogRetentionDays"
                  type="number"
                  min={90}
                  max={3650}
                  value={form.auditLogRetentionDays}
                  onChange={(e) => handleNumberChange('auditLogRetentionDays', e.target.value)}
                  className={cn(errors.auditLogRetentionDays && 'border-red-500')}
                />
                {errors.auditLogRetentionDays && (
                  <p className="text-xs text-red-500">{errors.auditLogRetentionDays}</p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Compliance Frameworks */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Compliance Frameworks</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between py-2">
              <div>
                <Label htmlFor="gdprEnabled" className="font-medium">
                  GDPR Compliance
                </Label>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  Enable EU General Data Protection Regulation features
                </p>
              </div>
              <Switch
                id="gdprEnabled"
                checked={form.gdprEnabled}
                onCheckedChange={(checked) => handleChange('gdprEnabled', checked)}
              />
            </div>

            <div className="flex items-center justify-between py-2">
              <div>
                <Label htmlFor="hipaaEnabled" className="font-medium">
                  HIPAA Compliance
                </Label>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  Enable Health Insurance Portability features
                </p>
              </div>
              <Switch
                id="hipaaEnabled"
                checked={form.hipaaEnabled}
                onCheckedChange={(checked) => handleChange('hipaaEnabled', checked)}
              />
            </div>

            <div className="flex items-center justify-between py-2">
              <div>
                <Label htmlFor="ccpaEnabled" className="font-medium">
                  CCPA Compliance
                </Label>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  Enable California Consumer Privacy Act features
                </p>
              </div>
              <Switch
                id="ccpaEnabled"
                checked={form.ccpaEnabled}
                onCheckedChange={(checked) => handleChange('ccpaEnabled', checked)}
              />
            </div>
          </CardContent>
        </Card>

        {/* Privacy Settings */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Privacy</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between py-2">
              <div>
                <Label htmlFor="anonymizeDeletedUsers" className="font-medium">
                  Anonymize Deleted Users
                </Label>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  Replace personal data with anonymous identifiers on deletion
                </p>
              </div>
              <Switch
                id="anonymizeDeletedUsers"
                checked={form.anonymizeDeletedUsers}
                onCheckedChange={(checked) => handleChange('anonymizeDeletedUsers', checked)}
              />
            </div>

            <div className="flex items-center justify-between py-2">
              <div>
                <Label htmlFor="requireConsentForMarketing" className="font-medium">
                  Require Marketing Consent
                </Label>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  Require explicit consent for marketing communications
                </p>
              </div>
              <Switch
                id="requireConsentForMarketing"
                checked={form.requireConsentForMarketing}
                onCheckedChange={(checked) => handleChange('requireConsentForMarketing', checked)}
              />
            </div>
          </CardContent>
        </Card>

        {/* Security Settings */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Security</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between py-2">
              <div>
                <Label htmlFor="enforcePasswordPolicy" className="font-medium">
                  Enforce Password Policy
                </Label>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  Require strong passwords for all users
                </p>
              </div>
              <Switch
                id="enforcePasswordPolicy"
                checked={form.enforcePasswordPolicy}
                onCheckedChange={(checked) => handleChange('enforcePasswordPolicy', checked)}
              />
            </div>

            {form.enforcePasswordPolicy && (
              <div className="pl-4 border-l-2 border-slate-200 dark:border-slate-700 space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="passwordMinLength">Minimum Length</Label>
                  <Input
                    id="passwordMinLength"
                    type="number"
                    min={8}
                    max={32}
                    value={form.passwordMinLength}
                    onChange={(e) => handleNumberChange('passwordMinLength', e.target.value)}
                    className={cn('w-24', errors.passwordMinLength && 'border-red-500')}
                  />
                  {errors.passwordMinLength && (
                    <p className="text-xs text-red-500">{errors.passwordMinLength}</p>
                  )}
                </div>

                <div className="flex items-center gap-6 flex-wrap">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={form.passwordRequireUppercase}
                      onChange={(e) => handleChange('passwordRequireUppercase', e.target.checked)}
                      className="rounded border-slate-300"
                    />
                    <span className="text-sm">Require uppercase</span>
                  </label>

                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={form.passwordRequireNumber}
                      onChange={(e) => handleChange('passwordRequireNumber', e.target.checked)}
                      className="rounded border-slate-300"
                    />
                    <span className="text-sm">Require number</span>
                  </label>

                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={form.passwordRequireSpecial}
                      onChange={(e) => handleChange('passwordRequireSpecial', e.target.checked)}
                      className="rounded border-slate-300"
                    />
                    <span className="text-sm">Require special character</span>
                  </label>
                </div>
              </div>
            )}

            <div className="grid grid-cols-3 gap-4 pt-4">
              <div className="space-y-2">
                <Label htmlFor="sessionTimeoutMinutes">Session Timeout (min)</Label>
                <Input
                  id="sessionTimeoutMinutes"
                  type="number"
                  min={15}
                  max={1440}
                  value={form.sessionTimeoutMinutes}
                  onChange={(e) => handleNumberChange('sessionTimeoutMinutes', e.target.value)}
                  className={cn(errors.sessionTimeoutMinutes && 'border-red-500')}
                />
                {errors.sessionTimeoutMinutes && (
                  <p className="text-xs text-red-500">{errors.sessionTimeoutMinutes}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="maxFailedLoginAttempts">Max Failed Logins</Label>
                <Input
                  id="maxFailedLoginAttempts"
                  type="number"
                  min={3}
                  max={10}
                  value={form.maxFailedLoginAttempts}
                  onChange={(e) => handleNumberChange('maxFailedLoginAttempts', e.target.value)}
                  className={cn(errors.maxFailedLoginAttempts && 'border-red-500')}
                />
                {errors.maxFailedLoginAttempts && (
                  <p className="text-xs text-red-500">{errors.maxFailedLoginAttempts}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="lockoutDurationMinutes">Lockout Duration (min)</Label>
                <Input
                  id="lockoutDurationMinutes"
                  type="number"
                  min={5}
                  max={60}
                  value={form.lockoutDurationMinutes}
                  onChange={(e) => handleNumberChange('lockoutDurationMinutes', e.target.value)}
                  className={cn(errors.lockoutDurationMinutes && 'border-red-500')}
                />
                {errors.lockoutDurationMinutes && (
                  <p className="text-xs text-red-500">{errors.lockoutDurationMinutes}</p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Audit Settings */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Audit Logging</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between py-2">
              <div>
                <Label htmlFor="logAllApiCalls" className="font-medium">
                  Log All API Calls
                </Label>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  Record all API requests (increases storage usage)
                </p>
              </div>
              <Switch
                id="logAllApiCalls"
                checked={form.logAllApiCalls}
                onCheckedChange={(checked) => handleChange('logAllApiCalls', checked)}
              />
            </div>

            <div className="flex items-center justify-between py-2">
              <div>
                <Label htmlFor="logSensitiveDataAccess" className="font-medium">
                  Log Sensitive Data Access
                </Label>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  Track access to PII and sensitive information
                </p>
              </div>
              <Switch
                id="logSensitiveDataAccess"
                checked={form.logSensitiveDataAccess}
                onCheckedChange={(checked) => handleChange('logSensitiveDataAccess', checked)}
              />
            </div>
          </CardContent>
        </Card>

        {/* Actions */}
        <div className="flex items-center justify-end pt-4">
          <Button type="submit" disabled={isSaving}>
            {isSaving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Shield className="w-4 h-4 mr-2" />
                Save Compliance Settings
              </>
            )}
          </Button>
        </div>
      </div>
    </form>
  );
}
